import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { SpaceMembershipOperation } from '../../api/Rest/space';
import { AlbumCellProps } from '../../components/Molecules';
import { RecentAlbumListTemplate } from '../../components/Templates';
import { SpacePrivacyPolicy } from '../../constants/space';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

const { fetchSpace, changeUserSpaceMembership } = useSpaceStore.getState();

const RecentAlbumListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const activeUserId = useSessionStore((store) => store.activeUserId);
  const [isSpaceLoading, setIsSpaceLoading] = useState(true);

  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  useRedirectEntry();

  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setIsSpaceLoading(false);
        });
    }
  }, [classroomId, spaceListFieldName]);

  const { classroom, classroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(classroomId ?? '');
    const membership = store.getActiveUserMembership(classroomId ?? '');
    return {
      classroom: space,
      classroomMembership: membership,
    };
  }, shallow);

  const isAllowToAccessClub =
    (classroom?.privacyPolicy === SpacePrivacyPolicy.Public ||
      (classroom?.privacyPolicy === SpacePrivacyPolicy.PrivateAndVisible &&
        classroomMembership?.isInGroup)) ??
    false;

  useRedirectHome(!isAllowToAccessClub && !isSpaceLoading);

  // TODO: need to be async?
  const visitClassroom = useCallback(() => {
    changeUserSpaceMembership(classroomId ?? '', {
      operation: SpaceMembershipOperation.Visit,
    }).catch(console.error);
  }, [classroomId]);

  useEffect(() => {
    if (classroomMembership === null) {
      visitClassroom();
    }
  }, [classroomMembership, visitClassroom]);

  // MARK: Club Recent Albums
  const onClickAlbum = useCallback(
    (gameSetId: string) => {
      navigate(`/club/${classroomId}/journey/${gameSetId}`, {
        state: { withBackState: true },
      });
    },
    [navigate, classroomId],
  );

  const recentAlbumFieldName = `${classroomId}_recent`;
  const recentAlbumField = useSpaceStoreListField(
    'albums',
    recentAlbumFieldName,
  );

  const fetchRecentAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchRecentAlbums(
        {
          fieldName: recentAlbumFieldName,
          classroomId: classroomId ?? '',
          withAlbumSummaries: true,
        },
        cursor,
      ),
    [classroomId, recentAlbumFieldName],
  );
  const {
    fetchMore: fetchMoreAlbums,
    refresh: refreshClubRecentAlbums,
    hasCursor,
  } = useFetchList(recentAlbumField, fetchRecentAlbums);

  useEffect(() => {
    if (classroomId) {
      refreshClubRecentAlbums().catch(console.error);
    }
  }, [classroomId, refreshClubRecentAlbums]);

  const loadMoreItems = useCallback(() => {
    fetchMoreAlbums().catch(console.error);
  }, [fetchMoreAlbums]);

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(recentAlbumFieldName);
  }, arrayEqual(shallow));

  const albumSummaries = useRecordStore((store) => {
    const gameSetIds = albums.map((item) => item.gameSetId);

    return gameSetIds.map((gameSetId) =>
      store.getAlbumSummary(gameSetId, activeUserId ?? ''),
    );
  }, arrayEqual(shallow));

  const albumData = useMemo<AlbumCellProps[]>(() => {
    return albums.map((album) => {
      const albumSummary = albumSummaries.find(
        (item) => item?.gameSetId === album.gameSetId,
      );
      return {
        album: album,
        albumSummary: albumSummary,
        albumClassroom: classroom,
        onClick: () => {
          onClickAlbum(album.gameSetId);
        },
      };
    });
  }, [albums, albumSummaries, classroom, onClickAlbum]);

  return userProfile ? (
    <RecentAlbumListTemplate
      albumData={albumData}
      hasCursor={hasCursor}
      loadMoreItems={loadMoreItems}
    />
  ) : null;
};

export default RecentAlbumListScreen;
